import type { PaymentKind } from '@krp/client-runtime'

import { formatKopecks } from '@krp/sale-ui'
import { toRubles } from './money'

/**
 * Оплата чека. Правила те же, что у кассового агента (Kalmykov-Cash-Agent, `AtolReceiptMapper.Validate`): окно проверяет их раньше,
 * чтобы кассир увидел причину до отправки, а агент повторяет проверку как последний рубеж. Сумма оплаты не меньше итога; безналичная
 * часть (карта, электронно) не больше итога; сдача возможна только с наличных и меньше внесённых наличных.
 */

export interface PaymentChoice {
  /** Внесено наличными, копейки (0 — нет). */
  cashKopecks: number
  /** Картой через терминал, копейки: агент сам проводит платёж. */
  cardKopecks: number
  /** Безналичной «электронно» (платёж проведён вне агента), копейки. */
  electronicallyKopecks: number
}

export const noPayment: PaymentChoice = { cashKopecks: 0, cardKopecks: 0, electronicallyKopecks: 0 }

export interface PaymentPlan {
  payments: readonly { type: PaymentKind; amount: number }[]
  /** Сдача кассира покупателю, копейки (только с наличных). */
  changeKopecks: number
  /** Есть оплата картой: чек будет ждать терминал. */
  hasCard: boolean
}

export type PaymentResult = { ok: true; plan: PaymentPlan } | { ok: false; text: string }

const money = formatKopecks

/**
 * Превратить выбор кассира в список оплат для агента или объяснить, почему нельзя. Итог 0 или меньше не оплачивается.
 */
export function planPayment(totalKopecks: number, choice: PaymentChoice): PaymentResult {
  if (!Number.isSafeInteger(totalKopecks) || totalKopecks <= 0) return { ok: false, text: 'Итог чека должен быть больше нуля.' }
  const parts: [PaymentKind, number][] = [
    ['cash', choice.cashKopecks],
    ['card', choice.cardKopecks],
    ['electronically', choice.electronicallyKopecks],
  ]
  for (const [, amount] of parts) {
    if (!Number.isSafeInteger(amount) || amount < 0) return { ok: false, text: 'Сумма оплаты не может быть отрицательной или дробной.' }
  }
  const nonCash = choice.cardKopecks + choice.electronicallyKopecks
  const paid = choice.cashKopecks + nonCash
  if (paid < totalKopecks) return { ok: false, text: `Оплата ${money(paid)} меньше итога ${money(totalKopecks)}.` }
  if (nonCash > totalKopecks) {
    return { ok: false, text: `Безналичная оплата ${money(nonCash)} больше итога ${money(totalKopecks)}: сдача возможна только с наличных.` }
  }
  const change = paid - totalKopecks
  // Сдача выдаётся наличными, поэтому она должна быть меньше внесённых наличных.
  if (change > 0 && change >= choice.cashKopecks) {
    return { ok: false, text: `Оплата ${money(paid)} не сходится с итогом ${money(totalKopecks)}: сдача возможна только с наличных.` }
  }
  const payments = parts.filter(([, amount]) => amount > 0).map(([type, amount]) => ({ type, amount: toRubles(amount) }))
  return { ok: true, plan: { payments, changeKopecks: change, hasCard: choice.cardKopecks > 0 } }
}

/** Только наличные: внесено столько-то. */
export const cashOnly = (cashKopecks: number): PaymentChoice => ({ ...noPayment, cashKopecks })

/** Всё картой: сумма равна итогу. */
export const cardOnly = (totalKopecks: number): PaymentChoice => ({ ...noPayment, cardKopecks: totalKopecks })
